// Work-order number — the one place the format lives.  #499, #502, Session 79.1
//
// WHAT THIS IS FOR, in plain terms: a field member receiving a delivery types a
// work order number the way they say it out loud — "WO-1234". The database
// refuses that, and before Session 79.1 the refusal was invisible: the sheet
// closed, no message appeared, and the delivery was never recorded. The person
// walked away believing the stock was in. This module is the rule they are
// being held to, stated once, so a screen can apply it BEFORE the press instead
// of the database applying it after.
//
// THE RULE IS NOT OURS TO CHOOSE — it is a copy of a CHECK constraint, and the
// two must not drift:
//
//   inventory_transactions.job_ref_format
//     CHECK (work_order_ref IS NULL OR work_order_ref ~ '^\d{6}(-\d+)?$')
//
// Read live off both databases on Sep 6, 2026 rather than taken from a comment.
// workOrderRef.test.js pins this pattern against that text, so changing one
// without the other fails the suite rather than shipping a form that accepts
// what the database will reject.
//
// EMPTY IS VALID, DELIBERATELY. The constraint allows NULL and the field is
// optional on every screen that offers it — #277 removed the requirement that a
// receive carry one. A validator that refused blank would make an optional field
// mandatory, which is a bigger change than the defect it was written to fix.
//
// WHY THIS IS SHARED RATHER THAN INLINE. The constrained column is written from
// FOUR screens — receiving stock, receiving a delivery against an order, and
// using stock from two different places — and a fifth writes it onto the REQUEST
// first, where no constraint exists at all (#502). One copy of the rule per
// screen is five chances to disagree with Postgres.

export const WORK_ORDER_REF_PATTERN = /^\d{6}(-\d+)?$/;

// Written for somebody holding a work order, not for a developer. It names the
// shape and then shows it, because "6 digits" alone leaves them guessing about
// the suffix they can see printed on their own paperwork.
//
// "REWORK" IS D&R'S OWN WORD FOR THE SUFFIX — Colby, Session 79.1: "work orders
// are supposed to have the six digit format or the six digits with a - and
// another number for rework. i.e. 123456 or 123456-7". The message the app shows
// is therefore in the vocabulary of the shop rather than the vocabulary of the
// schema; the existing wording called it a "sub-job", which is nobody's word for
// it here.
export const WORK_ORDER_REF_HINT =
  'Use the six-digit work order number, and add -# after it for a rework (e.g. 123456 or 123456-7).';

// Trimmed rather than rejected for stray spaces: a number pasted from an email
// or typed on a phone keyboard routinely carries one, and refusing that teaches
// nothing. Every caller submits the trimmed value.
export function normalizeWorkOrderRef(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function isValidWorkOrderRef(value) {
  const v = normalizeWorkOrderRef(value);
  if (v === '') return true;
  return WORK_ORDER_REF_PATTERN.test(v);
}

// Returns the message to show, or null when there is nothing to say. Callers
// render the string; they do not build their own, so the wording stays identical
// on all four screens.
export function workOrderRefError(value) {
  const v = normalizeWorkOrderRef(value);
  if (v === '') return null;
  if (WORK_ORDER_REF_PATTERN.test(v)) return null;
  return `"${v}" isn't a valid work order number. ${WORK_ORDER_REF_HINT}`;
}
